import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Map } from "lucide-react";

export default function FeesPage() {
  const [fees, setFees] = useState([]);
  useEffect(() => { api.get("/fees").then((r) => setFees(r.data)); }, []);

  const grouped = useMemo(() => {
    const byCat = {};
    fees.forEach((f) => {
      if (!byCat[f.category]) byCat[f.category] = [];
      byCat[f.category].push(f);
    });
    return byCat;
  }, [fees]);

  // Preferred display order — anything else falls under "Other"
  const order = ["Landing", "Ramp", "Hangar", "Hangar", "Tie-Down", "Other"];
  const categories = order.filter((c) => grouped[c]).concat(
    Object.keys(grouped).filter((c) => !order.includes(c))
  );

  return (
    <div data-testid="fees-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Layout & Fees</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-950 mt-3">
        Airport layout & fee schedule.
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Current published fees for landing, ramp, hangar, and ancillary services at Lake Cumberland Regional Airport (KSME).
        Most fees are waived with a qualifying fuel purchase — call ops to confirm.
      </p>

      {/* Layout block */}
      <section className="mt-12 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-7 bg-slate-50 border border-slate-200 overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_skyport-info/artifacts/ixjsutg7_runway.jpg"
            alt="KSME Airport Layout"
            className="w-full h-[360px] object-cover"
          />
        </div>
        <aside className="md:col-span-5 bg-slate-950 text-white p-8">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-sky-300">
            <Map className="h-4 w-4" /> Airport Layout
          </div>
          <h2 className="font-display text-2xl font-semibold mt-3">Field plan — Runway 5/23</h2>
          <p className="mt-3 text-slate-300 text-sm leading-relaxed">
            Single asphalt runway 5/23, 5,801 × 100 ft, with parallel taxiway, FBO ramp, T-hangar rows, box hangars,
            and a corporate hangar at the north apron. Self-serve fuel island is adjacent to the FBO.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-slate-300 border-t border-white/10 pt-5">
            <li className="flex gap-2"><span className="text-sky-300">→</span><span><span className="text-white font-medium">FBO</span> located off of Taxiway B and/or East Ramp.</span></li>
            <li className="flex gap-2"><span className="text-sky-300">→</span><span><span className="text-white font-medium">Self-Serve fuel farm</span> located off of Taxiway A and/or North Ramp.</span></li>
          </ul>
          <ul className="mt-6 space-y-2 text-sm font-mono-data">
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">Runway</span><span>5/23 · 5,801 × 100 ft</span></li>
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">Surface</span><span>Asphalt · MIRL</span></li>
            <li className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">Approaches</span><span>RNAV (GPS) 5 / 23 · ILS 5</span></li>
            <li className="flex justify-between"><span className="text-slate-400">FBO</span><span>South ramp</span></li>
          </ul>
        </aside>
      </section>

      {/* Fee tables */}
      <section className="mt-16 space-y-10">
        {categories.map((cat) => (
          <div key={cat}>
            <div className="text-xs font-mono uppercase tracking-widest text-sky-600">{cat} Fees</div>
            <div className="mt-4 border-t border-slate-200">
              {grouped[cat].map((f) => (
                <div
                  key={f.id}
                  data-testid={`fee-${f.id}`}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-4 border-b border-slate-200"
                >
                  <div className="sm:col-span-5">
                    <div className="font-display text-lg font-semibold text-slate-950">{f.name}</div>
                    {f.notes && <div className="text-sm text-slate-500 mt-1">{f.notes}</div>}
                  </div>
                  <div className="sm:col-span-4 font-mono-data text-slate-700 text-sm self-center">
                    {f.unit && <span className="text-slate-500">{f.unit}</span>}
                  </div>
                  <div className="sm:col-span-3 font-display text-xl font-bold text-slate-950 self-center sm:text-right">
                    {f.amount}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {fees.length === 0 && <div className="text-slate-500">No fees published yet.</div>}
      </section>
    </div>
  );
}
